import { ScrollableArea } from "@nova/component-scrollable-area"
import { useFlattenedFilteredAndSortedLayerSidePanelData } from "../useSidePanelLayers"
import {
    getGenericListItemWrapper,
    getStickyEntityListItemWrapper,
} from "../generics/GenericListItemWrapper"
import { DragAndDroppableListBuffer } from "../dragAndDrop"
import { SidePanelListEmptyState } from "../SidePanelListEmptyState"
import { useFilteredAndSortedStickyLayerSidePanelData } from "../useSidePanelStickyLayers"
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setActiveContextMenu } from "features/popups/slice"
import { clearSelection, selectSelectedLayerIds, setRenamingLayerId } from "../slice.new"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import * as Icons from "@nova/component-icons"
import { useCreateGroup } from "../generics/genericMultipleSelectedContextMenuActions"
import { GroupType } from "@nova/constants"
import classNames from "classnames"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { useArchivedLayerCount } from "../useSidePanelArchivedLayers"
import { useVirtualizedList } from "tools/hooks/useVirtualizedList"
import { createPortal } from "react-dom"
import { dataSelectorsByMode } from "../constants"
import { Resizable } from "re-resizable"
import { showGlobalModal } from "runlevel/actions"

const minStickyLayersListHeight = 64
const minLayersListHeight = 56

const ListItemWrapper = getGenericListItemWrapper({
    mode: "Layers",
})
const StickyListItemWrapper = getStickyEntityListItemWrapper()

export function SidePanelLayersList() {
    const dispatch = useAppDispatch()
    const [hoveredId, setHoveredId] = useState<string | undefined>(undefined)

    const flattenedLayers = useFlattenedFilteredAndSortedLayerSidePanelData()
    const { parentRef: layersParentRef, virtualizer: layerVirtualizer } =
        useVirtualizedList(flattenedLayers)

    const stickyLayers = useFilteredAndSortedStickyLayerSidePanelData()
    const { parentRef: stickyLayersParentRef, virtualizer: stickyLayerVirtualizer } =
        useVirtualizedList(stickyLayers)

    const activeSidePanelContextMenu = useAppSelector(selectActiveContextMenu("SidePanelList"))
    const onContextMenu = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            dispatch(
                setActiveContextMenu({
                    type: "SidePanelList",
                    data: {
                        mode: "Layers",
                    },
                    position: { left: e.clientX, top: e.clientY },
                })
            )
        },
        [dispatch]
    )
    useEffect(() => {
        if (typeof window === "undefined") return
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return
            dispatch(clearSelection())
        }
        window.addEventListener("keydown", handleEscape)
        return () => window.removeEventListener("keydown", handleEscape)
    }, [dispatch])
    const { handleCreateEmptyGroup } = useCreateGroup({
        mode: "Layers",
        groupType: GroupType.Layer,
    })
    const archivedLayerCount = useArchivedLayerCount()

    const sortedEntities = dataSelectorsByMode["Layers"].useSortedEntities()
    const selectedLayerIds = useAppSelector(selectSelectedLayerIds)

    const [maxStickyLayersListHeight, setMaxStickyLayersListHeight] = useState(0)
    const parentContainerRef = useRef<HTMLDivElement>(null)
    const stickyLayerVirtualizerTotalSize = stickyLayerVirtualizer.getTotalSize()
    useLayoutEffect(() => {
        const parentElement = parentContainerRef.current
        if (!parentElement) return
        const parentHeight = parentElement.getBoundingClientRect().height
        const actualMinLayersListHeight = !!flattenedLayers.length ? minLayersListHeight : 0
        const maxStickyLayersListHeight = Math.min(
            stickyLayerVirtualizerTotalSize + 6, // the +6 accounts for the resize handle
            parentHeight - actualMinLayersListHeight
        )
        setMaxStickyLayersListHeight(maxStickyLayersListHeight)
    }, [flattenedLayers.length, stickyLayerVirtualizer, stickyLayerVirtualizerTotalSize])

    return (
        <div
            ref={parentContainerRef}
            className="flex-1 min-h-0 flex flex-col"
            onContextMenu={onContextMenu}
            onClick={() => {
                dispatch(clearSelection())
                dispatch(setActiveContextMenu(undefined))
                dispatch(setRenamingLayerId({ id: undefined }))
            }}
        >
            {!!stickyLayers.length && (
                <Resizable
                    className="mb-3"
                    enable={{
                        top: false,
                        right: false,
                        left: false,
                        topRight: false,
                        bottomRight: false,
                        topLeft: false,
                        bottomLeft: false,
                        bottom: !!flattenedLayers.length,
                    }}
                    handleComponent={{
                        bottom: (
                            <div className="w-full h-full group flex justify-end px-2">
                                <div className="bg-gray-700 group-hover:bg-blue-700 w-full h-[2px] rounded-full group-hover:opacity-100 transition-opacity duration-100" />
                            </div>
                        ),
                    }}
                    handleStyles={{
                        bottom: {
                            bottom: "-16px",
                        },
                    }}
                    minHeight={minStickyLayersListHeight}
                    maxHeight={maxStickyLayersListHeight}
                >
                    <ScrollableArea
                        ref={stickyLayersParentRef}
                        className="px-3 h-full"
                        shouldPreventScrollbarShifting
                    >
                        <DragAndDroppableListBuffer />
                        <div
                            style={{
                                height: `${stickyLayerVirtualizerTotalSize}px`,
                                width: "100%",
                                position: "relative",
                            }}
                        >
                            {stickyLayerVirtualizer.getVirtualItems().map((virtualRow) => {
                                const item = stickyLayers[virtualRow.index]
                                return (
                                    <div
                                        key={item.id}
                                        data-index={virtualRow.index}
                                        ref={stickyLayerVirtualizer.measureElement}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            transform: `translateY(${virtualRow.start}px)`,
                                            zIndex: selectedLayerIds[0] === item.id ? 2 : 1,
                                        }}
                                        onMouseEnter={() => setHoveredId(item.id)}
                                        onMouseLeave={() =>
                                            hoveredId === item.id && setHoveredId(undefined)
                                        }
                                    >
                                        <StickyListItemWrapper
                                            item={item}
                                            sortedEntities={sortedEntities}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                        <DragAndDroppableListBuffer />
                    </ScrollableArea>
                </Resizable>
            )}
            {!!flattenedLayers.length && (
                <ScrollableArea
                    ref={layersParentRef}
                    className="grow px-3"
                    shouldPreventScrollbarShifting
                >
                    <DragAndDroppableListBuffer />
                    <div
                        style={{
                            height: `${layerVirtualizer.getTotalSize()}px`,
                            width: "100%",
                            position: "relative",
                        }}
                    >
                        {layerVirtualizer.getVirtualItems().map((virtualRow) => {
                            const item = flattenedLayers[virtualRow.index]
                            return (
                                <div
                                    key={item.id}
                                    data-index={virtualRow.index}
                                    ref={layerVirtualizer.measureElement}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        transform: `translateY(${virtualRow.start}px)`,
                                        zIndex: selectedLayerIds[0] === item.id ? 2 : 1,
                                    }}
                                    onMouseEnter={() => setHoveredId(item.id)}
                                    onMouseLeave={() =>
                                        hoveredId === item.id && setHoveredId(undefined)
                                    }
                                >
                                    <ListItemWrapper
                                        item={item}
                                        hoveredItemId={hoveredId}
                                        sortedEntities={sortedEntities}
                                    />
                                </div>
                            )
                        })}
                    </div>
                    <DragAndDroppableListBuffer />
                </ScrollableArea>
            )}
            {!stickyLayers.length && !flattenedLayers.length && <SidePanelListEmptyState />}
            {!!archivedLayerCount && (
                <>
                    <div className="grow" />
                    <div className="px-3 mt-1">
                        <div
                            className={classNames(
                                "w-full flex justify-center rounded-full px-3 py-0.5",
                                "bg-black-900 text-white-400 hover:text-white-1000 transition-colors",
                                "cursor-pointer"
                            )}
                            onClick={() => {
                                dispatch(
                                    initializeCenterModalForComponentAction(
                                        ModalNames.UnarchiveLayers
                                    )
                                )
                                dispatch(showGlobalModal())
                            }}
                        >
                            <small>
                                {archivedLayerCount === 1
                                    ? "1 Archived Layer"
                                    : `${archivedLayerCount} Archived Layers`}
                            </small>
                        </div>
                    </div>
                </>
            )}
            {activeSidePanelContextMenu?.data.mode === "Layers" &&
                createPortal(
                    <ContextMenu
                        sections={[
                            [
                                {
                                    title: "New Group",
                                    Icon: Icons.Folder,
                                    onClick: handleCreateEmptyGroup,
                                },
                            ],
                        ]}
                        onBlur={() => dispatch(setActiveContextMenu(undefined))}
                        isFixedPosition={true}
                        top={activeSidePanelContextMenu.position.top}
                        left={activeSidePanelContextMenu.position.left}
                    />,
                    document.getElementById("portal")!
                )}
        </div>
    )
}
